import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, AuthError } from 'firebase/auth'
import { IoLogoGoogle } from 'react-icons/io5'
import { auth } from 'src/config/firebaseConfig'

export const Login = () => {
  const navigate = useNavigate();

  const [loggingIn, setLoggingIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error: AuthError): string => {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in cancelled.';
      case 'auth/popup-blocked':
        return 'Popup was blocked. Please allow popups for this site.';
      default:
        return 'An error occurred during sign-in. Please try again.';
    }
  };

  const signInWithGoogle = async () => {
    setLoggingIn(true);
    setError('');

    try {
      const response = await signInWithPopup(auth, new GoogleAuthProvider());
      console.log("User signed in: ", response.user.uid);
      navigate('/');
    } catch (err) {
      const error = err as AuthError;
      console.error('Google sign-in error:', error.code);
      setError(getErrorMessage(error));
      setLoggingIn(false);
    }
  }

  const validator = (email: string, password: string) => {
    // Sanitize inputs
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      setError('Please fill in all fields');
      return false;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  }

  const signInWithEmail = async () => {
    setLoggingIn(true);
    setError('');

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    if (!validator(trimmedEmail, trimmedPassword)) {
      setLoggingIn(false);
      return;
    }

    try {
      const response = await signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
      console.log("User signed in: ", response.user.uid);
      navigate('/');
    } catch (err) {
      const error = err as AuthError;
      console.error('Email sign-in error:', error.code);
      setError(getErrorMessage(error));
      setLoggingIn(false);
    }
  }

  return (
    <div className='bg-gradient-to-br from-yellow-50 via-white to-gray-50 w-full min-h-screen flex items-center justify-center p-4'>
      <div className="w-full max-w-md">
        <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-zinc-800 mb-2">Welcome Back!</h1>
            <p className="text-gray-600">Enter your credentials to log in to your account</p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input 
                type="email"
                placeholder='you@example.com'
                className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-all duration-200 outline-none'
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                placeholder='••••••••'
                className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-all duration-200 outline-none'
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className='flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm'>
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Sign in button */}
            <button 
              className={`w-full px-4 py-3 rounded-lg font-semibold transition-all duration-200 shadow-sm hover:shadow-md ${
                loggingIn 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-zinc-800 to-zinc-900 hover:from-zinc-700 hover:to-zinc-800 text-white'
              }`}
              onClick={signInWithEmail}
              disabled={loggingIn}
            >
              {loggingIn ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging In...
                </span>
              ) : 'Log In'}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Google sign in button */}
            <button 
              className='flex items-center justify-center w-full px-4 py-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg gap-3 transition-all duration-200 shadow-sm hover:shadow-md'
              onClick={signInWithGoogle}
              disabled={loggingIn}
            >
              <IoLogoGoogle className="text-xl" />
              Sign In with Google
            </button>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <a href="/register" className='text-yellow-600 hover:text-yellow-700 font-semibold hover:underline transition-colors duration-200'>
                Sign Up
              </a>
            </p>
          </div>
        </div>

        {/* Additional info */}
        <p className="mt-6 text-center text-sm text-gray-600">
          Protected by industry-standard security
        </p>
      </div>
    </div>
  )
}
